/**
 * Estimador de JITTER con memoria del pico.
 *
 * ## Por qué una media no sirve para dimensionar un buffer
 *
 * Un jitter-buffer no se dimensiona con lo que pasa *normalmente*: se dimensiona
 * con lo que pasa *de vez en cuando*. Si la red va a 110 ms y una vez cada cinco
 * segundos se va a 360, una media móvil apenas se entera —el pico es uno entre
 * trescientas muestras— y el buffer se queda corto justo en el instante en que
 * hacía falta. El jugador ve un congelón y un salto; la media, mientras tanto,
 * dice que todo va bien.
 *
 * Medido en este juego (ver el arnés de `scripts/bench-jitter.ts`): con la ruta
 * estable el hueco entre snapshots es de 33 ms y el tirón de la pelota es
 * inapreciable; con picos de +250 ms cada cinco segundos el hueco se va a 317 ms
 * — y ahí es donde el buffer tiene que estar ya crecido, no empezar a crecer.
 *
 * ## Lo que hace este estimador
 *
 * Sube AL INSTANTE y baja DESPACIO (lo que en audio se llama envolvente de
 * ataque/relajación). Un pico eleva la estimación en la misma muestra en que
 * ocurre, y la deja alta unos segundos: si el pico se repite —que es lo que hace
 * una ruta mala— el buffer ya está preparado. Si no se repite, el valor cae solo
 * y se recupera la latencia que sobraba.
 *
 * Es O(1) y sin reservas de memoria: dos números. La alternativa honesta —una
 * ventana con percentil de verdad— cuesta ordenar en cada muestra y no da una
 * respuesta mejor para esto, porque lo que interesa no es "el percentil 95
 * exacto" sino "cuánto me sorprendió la red hace poco".
 */

/** Cuánto tarda el pico en caer a la mitad, en muestras. A 30 snapshots/s, ~2 s. */
export const JITTER_PEAK_HALFLIFE = 60;

/** Peso de la media móvil (0..1). Alto = reacciona antes, ruidoso. */
const MEAN_ALPHA = 0.1;

export class JitterEstimator {
  /** Media móvil de la señal (el "centro" alrededor del cual se mide). */
  private meanValue = 0;
  /** Desviación media absoluta (el jitter "de todos los días"). */
  private devValue = 0;
  /** Pico reciente de la desviación: sube de golpe, baja con vida media. */
  private peakValue = 0;
  private started = false;
  private readonly decay: number;

  constructor(halfLife = JITTER_PEAK_HALFLIFE) {
    // Factor por muestra que reduce el pico a la mitad en `halfLife` muestras.
    this.decay = Math.pow(0.5, 1 / Math.max(1, halfLife));
  }

  /** Incorpora una muestra y devuelve la desviación observada en ella. */
  add(sample: number): number {
    if (!this.started) {
      this.started = true;
      this.meanValue = sample;
      return 0;
    }
    const dev = Math.abs(sample - this.meanValue);
    this.devValue += (dev - this.devValue) * MEAN_ALPHA;
    this.meanValue += (sample - this.meanValue) * MEAN_ALPHA;
    // ATAQUE instantáneo, RELAJACIÓN lenta. El orden importa: primero se deja
    // caer el pico anterior y después se compara, para que una muestra tranquila
    // no congele el valor alto para siempre.
    this.peakValue *= this.decay;
    if (dev > this.peakValue) this.peakValue = dev;
    return dev;
  }

  /**
   * Vuelve al estado inicial (sesión nueva, reconexión, pestaña que regresa).
   *
   * Con `seed` se arranca ya centrado en ese valor —lo que se sabe que va a
   * medir— en vez de gastar las primeras muestras en encontrarlo. Sin `seed`,
   * la próxima muestra será la que centre.
   */
  reset(seed?: number): void {
    this.started = seed !== undefined;
    this.meanValue = seed ?? 0;
    this.devValue = 0;
    this.peakValue = 0;
  }

  /** ¿Ha visto ya alguna muestra? */
  get ready(): boolean {
    return this.started;
  }

  /** Media móvil de la señal. */
  get mean(): number {
    return this.meanValue;
  }

  /** Jitter TÍPICO (desviación media). Lo que se siente casi siempre. */
  get typical(): number {
    return this.devValue;
  }

  /**
   * Jitter con el que hay que dimensionar: el pico reciente, nunca por debajo
   * del típico. Es el número que responde a "¿cuánto me puede sorprender la red
   * en los próximos segundos?".
   */
  get headroom(): number {
    return Math.max(this.devValue, this.peakValue);
  }
}
